import { create } from "zustand";
import {
  MESSAGE_POPUP_LAYOUT_KEY,
  readStoredLayout,
  type MessagePopupLayout
} from "./constants";
import { writeLocalStorage } from "shared/hooks/use-local-storage";

export type MessagePopupChannel = "chat" | "email" | "sms";

/**
 * Everything a trigger knows about the conversation it wants to open.
 *
 * Exactly one of `contactId` / `applicantId` is required: the inbox keys
 * conversations by CRM contact, but an applicant row may not have been linked to
 * one yet — the pop-up resolves that lazily. `applicationId` lets the header
 * offer the application's other applicants without a second lookup.
 */
export interface MessagePopupTarget {
  contactId?: number;
  applicantId?: number;
  applicationId?: number;
  leadId?: number;
  isRealtorLead?: boolean;
  /** Name to show in the header until the conversation resolves its own. */
  contactName?: string;
  channel?: MessagePopupChannel;
}

interface MessagePopupState {
  target: MessagePopupTarget | null;
  layout: MessagePopupLayout;
  open: (target: MessagePopupTarget) => void;
  close: () => void;
  setLayout: (layout: MessagePopupLayout) => void;
}

/**
 * The message pop-up (CRM-1133) is mounted once above the routes (see
 * providers/Routes.tsx) while every trigger that opens it lives inside whatever
 * page is on screen: the lead header, a leads-table row, the application
 * header, the applicants table, an applications-table row. Open state therefore
 * lives in a store rather than a context — a trigger can hand the pop-up a
 * conversation without the two sharing a parent, two triggers can never stack
 * two pop-ups, and navigating away leaves the conversation (and its unsent
 * draft) exactly where it was.
 */
export const useMessagePopupStore = create<MessagePopupState>((set) => ({
  target: null,
  layout: readStoredLayout(),
  // Opening on a second row while one is already open re-points the same
  // pop-up — it is the same gesture either way.
  open: (target) => set({ target }),
  close: () => set({ target: null }),
  setLayout: (layout) => {
    writeLocalStorage(MESSAGE_POPUP_LAYOUT_KEY, layout);
    set({ layout });
  }
}));
